import uuid from 'react-uuid';
import { FundInvestmentDetail } from 'pages/FundInvestments';
import {
  FUND_INVESTMENTS_SLUG,
  FUND_INVESTMENTS_TITLE_SINGULAR,
} from 'pages/FundInvestments/common/constants/fundInvestmentsBase';
import { getNumberValue, getSlugValue } from 'utilities';

// Titles
export const FUND_INVESTMENTS_SUMMARY_TITLE = 'Summary';
export const FUND_INVESTMENTS_SUMMARY_SLUG = getSlugValue(FUND_INVESTMENTS_SUMMARY_TITLE);
export const FUND_INVESTMENTS_INVESTMENT_VEHICLE_TITLE = 'Investment Vehicle';
export const FUND_INVESTMENTS_TOTAL_TITLE = 'Total';
export const FUND_INVESTMENTS_ADD_INVESTMENT_VEHICLE_TITLE = `Add ${FUND_INVESTMENTS_INVESTMENT_VEHICLE_TITLE}`;
export const FUND_INVESTMENTS_ADD_INVESTMENT_VEHICLE_SLUG = getSlugValue(FUND_INVESTMENTS_ADD_INVESTMENT_VEHICLE_TITLE);
export const FUND_INVESTMENTS_NO_FUND_SELECTED_TITLE = 'To proceed, ensure that all entries have a selected Fund';
export const FUND_INVESTMENTS_FUND_INVESTMENT_DETAIL_FIELD_ATTRIBUTES = 'fund_investments.fundinvestmentdetail';
export const FUND_INVESTMENTS_SUMMARY_CONTAINER_CLASSNAME = `${FUND_INVESTMENTS_SLUG}-${FUND_INVESTMENTS_SUMMARY_SLUG}-container`;

// Aliases
export const FUND_INVESTMENTS_SUMMARY_SPREADSHEET_TITLE_ALIAS = 'FUND_INVESTMENTS_SUMMARY_SPREADSHEET_TITLE';
export const FUND_INVESTMENTS_SUMMARY_SPREADSHEET_INVESTMENT_VEHICLE_ALIAS =
  'FUND_INVESTMENTS_SUMMARY_SPREADSHEET_INVESTMENT_VEHICLE';
export const FUND_INVESTMENTS_SUMMARY_SPREADSHEET_TOTAL_ALIAS = 'FUND_INVESTMENTS_SUMMARY_SPREADSHEET_TOTAL';

// Spreadsheet
export const FUND_INVESTMENTS_SUMMARY_SPREADSHEET_COLUMN_NAME = 'Column';
export const FUND_INVESTMENTS_SUMMARY_SPREADSHEET_COLUMN_NAME_PLURAL = 'Columns';
export const FUND_INVESTMENTS_SUMMARY_SPREADSHEET_TITLE = `${FUND_INVESTMENTS_TITLE_SINGULAR} ${FUND_INVESTMENTS_SUMMARY_TITLE}`;
export const FUND_INVESTMENTS_SUMMARY_SPREADSHEET_SLUG = getSlugValue(FUND_INVESTMENTS_SUMMARY_SPREADSHEET_TITLE);
export const FUND_INVESTMENTS_SUMMARY_SPREADSHEET_NAME = `${FUND_INVESTMENTS_SUMMARY_SPREADSHEET_SLUG}-spreadsheet`;

export const FUND_INVESTMENTS_SUMMARY_SPREADSHEET_TABLE_TERMS = Object.freeze({
  columnName: FUND_INVESTMENTS_SUMMARY_SPREADSHEET_COLUMN_NAME,
  pluralColumnName: FUND_INVESTMENTS_SUMMARY_SPREADSHEET_COLUMN_NAME_PLURAL,
  tableName: FUND_INVESTMENTS_SUMMARY_SPREADSHEET_NAME,
  tableSlug: FUND_INVESTMENTS_SUMMARY_SPREADSHEET_SLUG,
});

// Columns
export const FUND_INVESTMENTS_SUMMARY_SPREADSHEET_COLUMNS = Object.freeze({
  as_of_date: {
    column: 0,
    title: 'As of',
    tooltip: '',
  },
  capital_account_date: {
    column: 1,
    title: 'Capital Account Date',
    tooltip: '',
  },
  committed_capital: {
    column: 2,
    title: 'Total Committed Capital',
    tooltip: '',
  },
  invested_capital: {
    column: 3,
    title: 'Total Invested Capital',
    tooltip: '',
  },
  realized_income: {
    column: 4,
    title: 'Realized Income/(Loss)',
    tooltip: '',
  },
  unrealized_income: {
    column: 5,
    title: 'Unrealized Income/(Loss)',
    tooltip: '',
  },
  distributions: {
    column: 6,
    title: 'Distributions',
    tooltip: '',
  },
  net_capital: {
    column: 7,
    title: 'Net Capital',
    tooltip: '',
  },
  market_adjustment: {
    column: 8,
    title: 'Market Adjustment',
    tooltip: '',
  },
  concluded_fair_value_of_net_capital: {
    column: 9,
    title: 'Concluded Fair Value of Net Capital',
    tooltip: '',
  },
});

export const getFundInvestmentSummaryColumn = (columnNumber: number) => {
  const columnsArray = Object.values(FUND_INVESTMENTS_SUMMARY_SPREADSHEET_COLUMNS);

  return columnsArray?.[columnNumber];
};

// Default values
export const FUND_INVESTMENTS_SUMMARY_SPREADSHEET_ALLOW_NEGATIVE_VALUE = true;
export const FUND_INVESTMENTS_SUMMARY_SPREADSHEET_ROW_SPAN = 1;
export const FUND_INVESTMENTS_SUMMARY_SPREADSHEET_COLUMN_KEY_PREFIX = getSlugValue(
  FUND_INVESTMENTS_INVESTMENT_VEHICLE_TITLE
);
export const FUND_INVESTMENTS_SUMMARY_SPREADSHEET_MARKET_ADJUSTMENT_CLASSNAMES = Object.freeze({
  error: 'error-text',
  success: 'success-text',
});
export const FUND_INVESTMENTS_SUMMARY_SPREADSHEET_EXCLUDED_ROWS = Object.freeze([
  FUND_INVESTMENTS_SUMMARY_SPREADSHEET_TITLE_ALIAS,
  FUND_INVESTMENTS_SUMMARY_SPREADSHEET_INVESTMENT_VEHICLE_ALIAS,
]);
export const FUND_INVESTMENTS_SUMMARY_SPREADSHEET_EXCLUDED_ROWS_COUNT = getNumberValue(
  FUND_INVESTMENTS_SUMMARY_SPREADSHEET_EXCLUDED_ROWS.length
);

export const FUND_INVESTMENTS_SUMMARY_SPREADSHEET_NEW_INVESTMENT_VEHICLE = Object.freeze({
  capital_account_date: null,
  commited_capital: null,
  concluded_fair_value_of_net_capital: null,
  distributions: null,
  fund_investment_ownership: null,
  fund: null,
  id: null, // Assigned by the backend
  invested_capital: null,
  market_adjustment: 0, // This value is required
  net_capital: null,
  realized_income: null,
  unrealized_income: null,
  uuid: null,
} as FundInvestmentDetail);

export const getFundInvestmentNewInvestmentVehicle = (fundInvestmentOwnership: number) =>
  ({
    ...FUND_INVESTMENTS_SUMMARY_SPREADSHEET_NEW_INVESTMENT_VEHICLE,
    fund_investment_ownership: fundInvestmentOwnership,
    uuid: uuid(),
  }) as FundInvestmentDetail;
